/**
 * Task Type Section
 *
 * Dynamic task type selection and configuration for scheduled tasks.
 * Fetches available job types from the API and renders appropriate config fields.
 */

import { useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { Check, ChevronsUpDown, Database, Image, RefreshCw, Terminal, Loader2, Brain, Mail } from 'lucide-react';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from '../../ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Checkbox } from '../../ui/checkbox';
import { Button } from '../../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../../ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../ui/popover';
import { cn } from '../../../lib/utils';
import { getJobTypes, type JobTypeDefinition, type JobTypeParameter } from '../../../lib/api';
import type { SharePointJob } from '../../../lib/sharepoint-api';
import commonStoredProcedures from '../../../config/commonStoredProcedures.json';

// Type for stored procedure presets
interface StoredProcedurePreset {
  name: string;
  procedureName: string;
  description: string;
  timeout?: number;
  parameters?: Record<string, unknown>;
}

// Icon mapping for job types
const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  'database': Database,
  'refresh-cw': RefreshCw,
  'image': Image,
  'terminal': Terminal,
  'brain': Brain,
  'mail': Mail,
};

interface TaskTypeSectionProps {
  form: UseFormReturn<any>;
  sharePointJobs?: SharePointJob[];
  sharePointJobsLoading?: boolean;
}

export function TaskTypeSection({
  form,
  sharePointJobs,
  sharePointJobsLoading,
}: TaskTypeSectionProps) {
  const [open, setOpen] = useState(false);

  // Fetch job types from API
  const { data: jobTypesData, isLoading: isLoadingJobTypes } = useQuery({
    queryKey: ['jobTypes'],
    queryFn: getJobTypes,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  const jobTypes = jobTypesData?.jobTypes || [];
  const sqlSyncEntities = jobTypesData?.options?.sqlSyncEntities || [];
  const fieldSyncEntities = jobTypesData?.options?.fieldSyncEntities || [];
  const sentimentPipelines = jobTypesData?.options?.sentimentPipelines || [];
  const jobs = sharePointJobs || [];

  // Get the selected job type
  const selectedJobType = form.watch('jobType');
  const selectedJobTypeDef = jobTypes.find(jt => jt.jobType === selectedJobType);

  // Get icon component for a job type
  const getIcon = (iconName: string) => {
    const IconComponent = ICON_MAP[iconName];
    return IconComponent ? <IconComponent className="h-4 w-4" /> : null;
  };

  // Render a parameter field based on its type
  const renderParameterField = (param: JobTypeParameter) => {
    const fieldName = `parameters.${param.name}`;

    switch (param.type) {
      case 'string':
        return (
          <FormField
            key={param.name}
            control={form.control}
            name={fieldName}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{param.label}{param.required && ' *'}</FormLabel>
                <FormControl>
                  <Input
                    placeholder={param.description}
                    {...field}
                    value={field.value ?? param.default ?? ''}
                  />
                </FormControl>
                {param.description && (
                  <FormDescription className="text-xs">{param.description}</FormDescription>
                )}
                <FormMessage />
              </FormItem>
            )}
          />
        );

      case 'number':
        return (
          <FormField
            key={param.name}
            control={form.control}
            name={fieldName}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{param.label}{param.required && ' *'}</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    placeholder={param.description}
                    {...field}
                    value={field.value ?? param.default ?? ''}
                    onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                  />
                </FormControl>
                {param.description && (
                  <FormDescription className="text-xs">{param.description}</FormDescription>
                )}
                <FormMessage />
              </FormItem>
            )}
          />
        );

      case 'boolean':
        return (
          <FormField
            key={param.name}
            control={form.control}
            name={fieldName}
            render={({ field }) => (
              <FormItem className="flex flex-row items-center space-x-3 space-y-0">
                <FormControl>
                  <Checkbox
                    checked={field.value ?? param.default ?? false}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <FormLabel className="font-normal cursor-pointer">
                  {param.label}
                </FormLabel>
              </FormItem>
            )}
          />
        );

      case 'select':
        // Determine options based on optionsSource
        let options = param.options || [];
        if (param.optionsSource === 'sqlSyncEntities') {
          options = sqlSyncEntities;
        } else if (param.optionsSource === 'fieldSyncEntities') {
          options = fieldSyncEntities;
        } else if (param.optionsSource === 'sentimentPipelines') {
          options = sentimentPipelines;
        } else if (param.optionsSource === 'sharePointJobs') {
          options = jobs.map(j => ({ value: j.id, label: j.name }));
        }

        return (
          <FormField
            key={param.name}
            control={form.control}
            name={fieldName}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{param.label}{param.required && ' *'}</FormLabel>
                <Select
                  onValueChange={(value) => field.onChange(value === '__none__' ? undefined : value)}
                  value={field.value ?? '__none__'}
                  disabled={param.optionsSource === 'sharePointJobs' && !!sharePointJobsLoading}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder={
                        param.optionsSource === 'sharePointJobs' && sharePointJobsLoading
                          ? 'Loading...'
                          : `Select ${param.label.toLowerCase()}`
                      } />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {!param.required && (
                      <SelectItem value="__none__">None</SelectItem>
                    )}
                    {options.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {param.description && (
                  <FormDescription className="text-xs">{param.description}</FormDescription>
                )}
                <FormMessage />
              </FormItem>
            )}
          />
        );

      case 'json':
        return (
          <FormField
            key={param.name}
            control={form.control}
            name={fieldName}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{param.label}{param.required && ' *'}</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder='{"key": "value"}'
                    className="font-mono text-sm"
                    rows={3}
                    {...field}
                    value={typeof field.value === 'object' ? JSON.stringify(field.value, null, 2) : (field.value ?? '')}
                    onChange={(e) => {
                      try {
                        const parsed = e.target.value ? JSON.parse(e.target.value) : undefined;
                        field.onChange(parsed);
                      } catch {
                        // Keep raw value if not valid JSON yet
                        field.onChange(e.target.value);
                      }
                    }}
                  />
                </FormControl>
                {param.description && (
                  <FormDescription className="text-xs">{param.description}</FormDescription>
                )}
                <FormMessage />
              </FormItem>
            )}
          />
        );

      default:
        return null;
    }
  };

  if (isLoadingJobTypes) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Task Type</CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center h-32">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Task Type</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Job Type Selector - Searchable Combobox */}
        <FormField
          control={form.control}
          name="jobType"
          render={({ field }) => (
            <FormItem className="flex flex-col">
              <FormLabel>Job Type *</FormLabel>
              <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                  <FormControl>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={open}
                      className={cn(
                        "w-full justify-between",
                        !field.value && "text-muted-foreground"
                      )}
                    >
                      {field.value ? (
                        <span className="flex items-center gap-2">
                          {selectedJobTypeDef && getIcon(selectedJobTypeDef.icon)}
                          {selectedJobTypeDef?.name || field.value}
                        </span>
                      ) : (
                        "Select job type..."
                      )}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </FormControl>
                </PopoverTrigger>
                <PopoverContent className="w-[400px] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search job types..." />
                    <CommandList>
                      <CommandEmpty>No job type found.</CommandEmpty>
                      {/* Group by category */}
                      {['sql', 'sharepoint', 'maintenance', 'sentiment'].map(category => {
                        const categoryTypes = jobTypes.filter(jt => jt.category === category);
                        if (categoryTypes.length === 0) return null;

                        const categoryLabel = {
                          sql: 'SQL Sync',
                          sharepoint: 'SharePoint',
                          maintenance: 'Maintenance',
                          sentiment: 'Sentiment Analysis'
                        }[category];

                        return (
                          <CommandGroup key={category} heading={categoryLabel}>
                            {categoryTypes.map((jobType) => (
                              <CommandItem
                                key={jobType.jobType}
                                value={jobType.jobType}
                                onSelect={() => {
                                  form.setValue('jobType', jobType.jobType);
                                  // Clear existing parameters when job type changes
                                  form.setValue('parameters', {});
                                  setOpen(false);
                                }}
                              >
                                <Check
                                  className={cn(
                                    "mr-2 h-4 w-4",
                                    field.value === jobType.jobType ? "opacity-100" : "opacity-0"
                                  )}
                                />
                                <div className="flex items-center gap-2">
                                  {getIcon(jobType.icon)}
                                  <div>
                                    <div className="font-medium">{jobType.name}</div>
                                    <div className="text-xs text-muted-foreground">
                                      {jobType.description}
                                    </div>
                                  </div>
                                </div>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        );
                      })}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Dynamic Parameters based on selected job type */}
        {selectedJobTypeDef && selectedJobTypeDef.parameters.length > 0 && (
          <div className="space-y-3 pt-3 border-t">
            <div className="text-sm font-medium text-muted-foreground">
              Configuration
            </div>
            {selectedJobTypeDef.parameters.map(param => renderParameterField(param))}
          </div>
        )}

        {/* Quick presets for Stored Procedure job type */}
        {selectedJobType === 'Sql.StoredProcedure' && commonStoredProcedures.procedures.length > 0 && (
          <div className="space-y-2 pt-3 border-t">
            <div className="text-sm font-medium text-muted-foreground">
              Common Procedures
            </div>
            <div className="grid gap-2">
              {(commonStoredProcedures.procedures as StoredProcedurePreset[]).map((preset) => (
                <button
                  key={preset.procedureName}
                  type="button"
                  onClick={() => {
                    form.setValue('parameters.procedureName', preset.procedureName);
                    if (preset.timeout) {
                      form.setValue('parameters.timeout', preset.timeout);
                    }
                    if (preset.parameters) {
                      form.setValue('parameters.parameters', preset.parameters);
                    }
                  }}
                  className="flex flex-col items-start p-3 text-left border rounded-lg hover:bg-accent/50 transition-colors"
                >
                  <span className="font-medium text-sm">{preset.name}</span>
                  <span className="text-xs text-muted-foreground font-mono">{preset.procedureName}</span>
                  {preset.description && (
                    <span className="text-xs text-muted-foreground mt-1">{preset.description}</span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
